import { WorkPolicyEditor } from "../../../work-type-management/work-policy-editor";
import { WorkingTemplateBasePolicyState } from "./working-template-base-policy.model";
import {
  EmptyIcon,
  PolicyPanel,
  SelectionState,
  SelectorCard,
  StatusPanel,
  WarningText
} from "./management-policy-work-time.styles";
import type { WorkingTemplateSaveStage } from "../../../../screens/view-settings/work-schedule-settings/add-update/working-template-save-orchestrator";
import Button from "../../../globalComponents/Button";
import WSBottomContainer from "../../w_s_bottom_container/w_s_bottom_container";

export interface ManagementPolicyWorkTimeProps {
  state: WorkingTemplateBasePolicyState;
  onSelect: (policyId: number | null) => void;
  onRetryCatalog: () => void;
  onRetryDetail: () => void;
  saveStage?: WorkingTemplateSaveStage;
  onRetryBinding?: () => void;
  onSave: () => void;
}

const noChange = () => undefined;
const noSearch = async () => [];

export const ManagementPolicyWorkTime = ({
  state,
  onSelect,
  onRetryCatalog,
  onRetryDetail,
  saveStage = "idle",
  onRetryBinding,
  onSave
}: ManagementPolicyWorkTimeProps) => {
  const catalogLoading =
    state.catalogStatus === "loading" || state.catalogStatus === "idle";
  const hasActivePolicies = state.options.some(option => option.value !== null);
  const unavailable =
    state.bindingStatus === "unavailable" ||
    state.detailStatus === "unavailable";
  const saving = saveStage === "basic-saving" || saveStage === "binding-saving";

  return (
    <PolicyPanel aria-label="근무유형 관리정책">
      <SelectorCard>
        <label htmlFor="working-template-base-policy">근무제 유형</label>
        <select
          id="working-template-base-policy"
          aria-describedby="working-template-base-policy-help working-template-base-policy-save-help"
          value={state.selectedPolicyId ?? ""}
          disabled={catalogLoading || saving}
          onChange={event =>
            onSelect(
              event.currentTarget.value === ""
                ? null
                : Number(event.currentTarget.value)
            )
          }
        >
          {state.options.map(option => (
            <option key={option.value ?? "unused"} value={option.value ?? ""}>
              {option.label}
            </option>
          ))}
        </select>
        <p id="working-template-base-policy-help">
          환경 설정 &gt; 근무 설정 &gt; 근무 유형 관리에 등록된 사용 중 기본
          정책만 선택할 수 있습니다.
        </p>
        <p id="working-template-base-policy-save-help">
          선택 변경은 근무 정책 추가/수정 창 하단의 저장을 눌러야 반영됩니다.
        </p>
      </SelectorCard>

      {saveStage === "basic-saving" && (
        <StatusPanel aria-live="polite">
          <h3>근무 정책 기본정보를 저장하고 있습니다.</h3>
          <p>기본정보 저장이 끝나면 기본 정책 연결을 이어서 저장합니다.</p>
        </StatusPanel>
      )}

      {saveStage === "binding-saving" && (
        <StatusPanel aria-live="polite">
          <h3>기본 정책 연결을 저장하고 있습니다.</h3>
          <p>저장이 완료될 때까지 창을 닫지 마세요.</p>
        </StatusPanel>
      )}

      {saveStage === "partial-failure" && (
        <StatusPanel $tone="danger" role="alert" tabIndex={-1}>
          <h3>
            근무 정책 기본정보는 저장되었지만 기본 정책 연결에 실패했습니다.
          </h3>
          <p>
            생성된 근무 정책은 유지됩니다. 같은 근무 정책에 연결 저장을 다시
            시도해 주세요.
          </p>
          {onRetryBinding && (
            <button type="button" onClick={onRetryBinding}>
              연결 저장 다시 시도
            </button>
          )}
        </StatusPanel>
      )}

      {catalogLoading && (
        <StatusPanel aria-live="polite">
          <h3>기본 정책을 불러오는 중입니다.</h3>
          <p>사용 가능한 정책 목록을 확인하고 있습니다.</p>
        </StatusPanel>
      )}

      {state.catalogStatus === "error" && (
        <StatusPanel $tone="danger" role="alert">
          <h3>기본 정책 목록을 불러오지 못했습니다.</h3>
          <p>{state.catalogError?.message}</p>
          <button type="button" onClick={onRetryCatalog}>
            목록 다시 시도
          </button>
        </StatusPanel>
      )}

      {state.catalogStatus === "success" && !hasActivePolicies && (
        <StatusPanel>
          <h3>선택 가능한 기본 정책이 없습니다.</h3>
          <p>
            미사용으로 저장하거나 환경설정에서 사용 중 기본 정책을 등록할 수
            있습니다.
          </p>
        </StatusPanel>
      )}

      {state.catalogStatus === "success" && state.selectedPolicyId === null && (
        <StatusPanel aria-labelledby="base-policy-unused-title">
          <EmptyIcon aria-hidden="true">◇</EmptyIcon>
          <h3 id="base-policy-unused-title">기본 정책 미사용</h3>
          <p>
            이 근무 정책에는 근무 유형 관리의 기본 정책을 연결하지 않습니다.
          </p>
          <SelectionState $changed={state.dirty}>
            {state.dirty ? "선택 변경" : "저장된 선택"}
          </SelectionState>
          {state.lastPersistedPolicyId !== null && (
            <WarningText>
              저장하면 기존에 연결된 기본 정책도 해제됩니다.
            </WarningText>
          )}
        </StatusPanel>
      )}

      {unavailable && (
        <StatusPanel $tone="danger" role="alert">
          <h3>현재 연결 정책을 사용할 수 없습니다.</h3>
          <p>
            {state.bindingError?.message ??
              "정책이 비활성화되었거나 접근 범위에서 제외되었습니다."}
          </p>
        </StatusPanel>
      )}

      {!unavailable &&
        state.detailStatus === "loading" &&
        state.selectedPolicyId !== null && (
          <WorkPolicyEditor
            presentation="embedded-readonly"
            value={null}
            errors={{}}
            loading
            saving={false}
            dirty={state.dirty}
            recovery={null}
            successMessage={null}
            onChange={noChange}
            onSave={noChange}
            onDelete={noChange}
            onRetry={noChange}
            searchDepartments={noSearch}
            searchEmployees={noSearch}
            confirmTargetChange={() => false}
          />
        )}

      {!unavailable && state.detailStatus === "error" && (
        <StatusPanel $tone="danger" role="alert">
          <h3>정책 상세를 불러오지 못했습니다.</h3>
          <p>{state.detailError?.message}</p>
          <button type="button" onClick={onRetryDetail}>
            상세 다시 시도
          </button>
        </StatusPanel>
      )}

      {!unavailable &&
        state.detailStatus === "success" &&
        state.detailSnapshot && (
          <WorkPolicyEditor
            presentation="embedded-readonly"
            value={state.detailSnapshot}
            errors={{}}
            loading={false}
            saving={false}
            dirty={state.dirty}
            recovery={null}
            successMessage={null}
            onChange={noChange}
            onSave={noChange}
            onDelete={noChange}
            onRetry={noChange}
            searchDepartments={noSearch}
            searchEmployees={noSearch}
            confirmTargetChange={() => false}
          />
        )}
      <WSBottomContainer>
        <Button
          maxWidth="100%"
          customMinWidth="100%"
          disabled={saving || catalogLoading}
          onClick={onSave}
        >
          저장
        </Button>
      </WSBottomContainer>
    </PolicyPanel>
  );
};
